/**
 * Relay GlobalID <-> bare primary key.
 *
 * The Vault stores a reference attribute's value as the BARE pk in
 * `document_attribute_values.value_id` — the write path decodes the GlobalID a
 * caller sends (`_validate_multi_id_value` in document-vault-backend rewrites it
 * in place, and `_resolve_arena_id` then reads it as `int(raw)`).
 *
 * The SEARCH path does not decode. `_resolve_any_of_ids` documents it plainly:
 *
 *   > `reference` values are opaque WIS ids (D5) — no option table, so they are
 *   > already the stored form and pass through.
 *
 * So `VaultDocumentAttributeFilter.textValues` for a reference attribute must
 * carry bare pks, while `ClientSearch` / `EntitySearch` (and every other relay
 * connection in main) hand back GlobalIDs. Decoding is therefore the caller's
 * job, and it happens here rather than at each call site — a GlobalID sent
 * verbatim is not an error, it simply matches nothing.
 *
 * Mirrors `fund-admin-frontend`'s `DataVault/Entities/utils/globalIdUtils`, and
 * the type names match document-vault-backend's `_REFERENCE_TARGET_TYPE_NAMES`
 * (`clients` -> ArenaObjectType, `entities` -> AccountObjectType,
 * `lp_investors` -> PositionObjectType per DV-572). LP filters therefore pass
 * **position** pks (not account pks) through `toBareReferenceId`.
 */

/** GlobalID type name for a client/arena reference. */
export const ARENA_TYPE_NAME = "ArenaObjectType";
/** GlobalID type name for an entity (`entities`) reference. */
export const ACCOUNT_TYPE_NAME = "AccountObjectType";
/** GlobalID type name for an LP-investor (`lp_investors`) reference. */
export const POSITION_TYPE_NAME = "PositionObjectType";

/** `{ type, id }` for a `base64("Type:pk")` GlobalID, or null if it isn't one. */
export const parseGlobalId = (globalId: string): { type: string; id: string } | null => {
  if (!globalId) return null;
  let decoded: string;
  try {
    decoded = atob(globalId);
  } catch {
    return null; // not base64 — already a bare id, or junk
  }
  const separator = decoded.indexOf(":");
  if (separator <= 0) return null;
  const type = decoded.slice(0, separator);
  const id = decoded.slice(separator + 1);
  // The pk is always numeric in main's registries; anything else means we
  // decoded something that merely looked like base64.
  if (!id || !/^\d+$/.test(id)) return null;
  return { type, id };
};

/**
 * The bare pk the Vault stores for a reference value.
 *
 * A value that is ALREADY bare passes through unchanged: the filter model can be
 * rehydrated from a URL or a saved view, and re-decoding an id we decoded once
 * would otherwise strip it to nothing.
 *
 * Type-agnostic: Arena / Account / Position GlobalIDs all reduce to the embedded
 * pk. Callers must still send the right *kind* of pk for the attribute
 * (`lp_investors` → position pk).
 */
export const toBareReferenceId = (value: string): string => parseGlobalId(value)?.id ?? value;

/** Encode a bare arena pk as a relay GlobalID for `ClientSearch` / upload payloads. */
export const toArenaGlobalId = (bareId: string): string => btoa(`${ARENA_TYPE_NAME}:${bareId}`);
